import { useQuery } from "@tanstack/react-query";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useCallback, useState } from "react";
import { If } from "../../../../components/If/If";
import { ImportExportData } from "../../../../components/import-export-data/import-export-data";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";
import { Loader } from "../../../../components/Loader/Loader";
import { SectionHeader } from "../../../../components/SectionHeader/SectionHeader";
import { debounce } from "../../../../helpers/debounce";
import { useFilterQuery } from "../../../../hooks/useFilterQuery";
import { requests } from "../../../../services/http-common";
import { IFilter } from "../../../../types";
import { CustomerCategoryEnum } from "../../SalesLead/Lead.model";
import { UserRoles } from "../../../../constants/userRoles";

import classes from "./ClientList.module.scss";
import { Actions } from "./columns/client-actions";
import { useEffectWithoutMount } from "../../../../helpers/helperHooks";

const statusOptions = [
  {
    label: "All",
    value: "",
  },
  {
    label: "Corporate",
    value: CustomerCategoryEnum.CORPORATE,
  },
  {
    label: "Individual",
    value: CustomerCategoryEnum.INDIVIDUAL,
  },
];

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByCustomerCategory: "",
  sortOrder: -1,
};

export const ClientList = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [term, setTerm] = useState("");
  const filterQuery = useFilterQuery(filters);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["client-list"],
    queryFn: () => {
      return requests.get(`/client?${filterQuery}`);
    },
    retry: false,
  });

  useEffectWithoutMount(() => {
    refetch();
  }, [filters, refetch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearch = useCallback(
    debounce((event: any) => {
      setFilters((prev) => ({ ...prev, filter: event.target.value }));
    }, 500),
    []
  );

  const handleInputSearch = (e: any) => {
    debouncedOnSearch(e);
    setTerm(e.target.value);
  };

  const onDropdownChange = (e: DropdownChangeParams) => {
    setFilters((prev) => ({ ...prev, [e.target.id]: e.value }));
  };

  const onPage = useCallback(
    (event: DataTablePFSEvent) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters]
  );

  return (
    <div className={classes.container}>
      <SectionHeader title="Clients" />
      <div className={classes.filters}>
        <LabelValue label="Customer Category">
          <Dropdown
            appendTo="self"
            options={statusOptions}
            id="filterByCustomerCategory"
            onChange={onDropdownChange}
            value={filters.filterByCustomerCategory}
            className={classes.dropdown}
          />
        </LabelValue>
        <div>
          <ImportExportData
            exportUrl={`/client/export-csv?${filterQuery}`}
            importUrl="/client/import-csv"
            refetchQury={refetch}
            viewRole={UserRoles.CLIENT_VIEW}
          />
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              onChange={handleInputSearch}
              value={term}
              placeholder="Search"
            />
          </span>
        </div>
      </div>
      <If condition={isLoading}>
        <Loader />
        <div className={classes.table}>
          <DataTable
            responsiveLayout="scroll"
            value={data?.result}
            paginator={true}
            paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
            first={filters.first}
            rows={filters.rows}
            totalRecords={data?.total}
            onPage={onPage}
            paginatorPosition="top"
            paginatorClassName={classes.paginator}
            lazy
          >
            <Column field="clientId" header="Client Number"></Column>
            <Column
              header="Client Name"
              body={(rowData: any) => {
                return (
                  <span>
                    {rowData.contactName
                      ? `${rowData.contactName}`
                      : `${rowData.name ?? ""} ${rowData.surname ?? ""}`}
                  </span>
                );
              }}
            ></Column>
            <Column field="companyName" header="Company"></Column>
            <Column field="email" header="Email Address"></Column>
            <Column field="phone" header="Phone Number"></Column>
            <Column
              header="Actions"
              body={(rowData: {
                id: string;
                isOneTimePasswordSent: boolean;
              }) => <Actions {...rowData} />}
            ></Column>
          </DataTable>
        </div>
      </If>
    </div>
  );
};
